import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

const ProductServicesPage = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    
    // Current service being edited or viewed
    const [currentService, setCurrentService] = useState({
        nama_layanan: '',
        deskripsi: '',
        icon_url: '',
        durasi_menit: 60,
        harga_dasar: 0,
        kategori: '',
        is_active: true,
        variants: []
    });
    
    // Current variant being edited
    const [currentVariant, setCurrentVariant] = useState({
        durasi_menit: 0,
        harga_dasar: 0,
        deskripsi_variant: '',
        is_active: true
    });
    
    // Filter & search state
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [kategoriFilter, setKategoriFilter] = useState('all');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(8);
    
    // API config
    const API_BASE_URL = 'https://api.tomassage.id/api';
    const UPLOAD_URL = 'https://ygtechdev.my.id/upload';
    
    // Fetch services data
    const fetchServices = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/services`);
            setServices(response.data.data || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching services:', err);
            setError('Gagal memuat data layanan: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };
    
    // Fetch service with variants
    const fetchServiceWithVariants = async (serviceId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/services/${serviceId}/variants`);
            return response.data.data;
        } catch (err) {
            console.error('Error fetching service variants:', err);
            throw new Error('Gagal memuat varian layanan: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Initial fetch
    useEffect(() => {
        fetchServices();
    }, []);
    
    // Handle image selection
    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            const objectUrl = URL.createObjectURL(file);
            setImagePreview(objectUrl);
        }
    };
    
    // Upload image
    const uploadImage = async (file) => {
        if (!file) return null;
        
        const formData = new FormData();
        formData.append('photo', file);
        
        try {
            const response = await axios.post(UPLOAD_URL, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            const fileUrl = response.data.fileUrl || response.data.url || response.data.photo_url;
            
            if (!fileUrl) {
                throw new Error('Server did not return a file URL');
            }
            
            return fileUrl;
        } catch (err) {
            console.error('Error uploading image:', err);
            throw new Error(`Failed to upload image: ${err.message}`);
        }
    };
    
    // Format price
    const formatPrice = (price) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };
    
    // Reset form
    const resetForm = () => {
        setCurrentService({
            nama_layanan: '',
            deskripsi: '',
            icon_url: '',
            durasi_menit: 60,
            harga_dasar: 0,
            kategori: '',
            is_active: true,
            variants: []
        });
        setSelectedImage(null);
        setImagePreview(null);
        setError(null);
    };
    
    // Reset variant form
    const resetVariantForm = () => {
        setCurrentVariant({
            durasi_menit: 0,
            harga_dasar: 0,
            deskripsi_variant: '',
            is_active: true
        });
    };
    
    // Handle add service
    const handleAddService = async (e) => {
        e.preventDefault();
        try {
            setError(null);
            
            if (!selectedImage) {
                setError('Gambar icon layanan wajib diupload');
                return;
            }
            
            // Upload image
            let iconUrl = await uploadImage(selectedImage);
            
            const serviceData = {
                ...currentService,
                icon_url: iconUrl,
                harga_dasar: parseInt(currentService.harga_dasar),
                durasi_menit: parseInt(currentService.durasi_menit)
            };
            
            // Remove variants from the service data
            const { variants, ...serviceDataWithoutVariants } = serviceData;
            
            const response = await axios.post(`${API_BASE_URL}/services`, serviceDataWithoutVariants);
            
            if (response.data.success || response.status === 201) {
                await fetchServices();
                resetForm();
                setSuccessMessage('Layanan berhasil ditambahkan');
                
                // Close modal
                document.getElementById('addServiceModalClose').click();
            }
        } catch (err) {
            console.error('Error adding service:', err);
            setError('Gagal menambahkan layanan: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Handle edit service
    const handleEditService = async (e) => {
        e.preventDefault();
        try {
            setError(null);
            
            let iconUrl = currentService.icon_url;
            
            // Upload new image if selected
            if (selectedImage) {
                iconUrl = await uploadImage(selectedImage);
            }
            
            const serviceData = {
                ...currentService,
                icon_url: iconUrl,
                harga_dasar: parseInt(currentService.harga_dasar),
                durasi_menit: parseInt(currentService.durasi_menit)
            };
            
            // Remove variants from the service data
            const { variants, ...serviceDataWithoutVariants } = serviceData;
            
            const response = await axios.put(`${API_BASE_URL}/services/${currentService.id}`, serviceDataWithoutVariants);
            
            if (response.data.success || response.status === 200) {
                await fetchServices();
                resetForm();
                setSuccessMessage('Layanan berhasil diperbarui');
                
                // Close modal
                document.getElementById('editServiceModalClose').click();
            }
        } catch (err) {
            console.error('Error updating service:', err);
            setError('Gagal memperbarui layanan: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Handle delete service
    const handleDeleteService = async () => {
        try {
            setError(null);
            
            const response = await axios.delete(`${API_BASE_URL}/services/${currentService.id}`);
            
            if (response.data.success || response.status === 200) {
                await fetchServices();
                resetForm();
                setSuccessMessage('Layanan berhasil dihapus');
                
                // Close modal
                document.getElementById('deleteServiceModalClose').click();
            }
        } catch (err) {
            console.error('Error deleting service:', err);
            setError('Gagal menghapus layanan: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Toggle service active status
    const toggleServiceStatus = async (service) => {
        try {
            const updatedService = {
                ...service,
                is_active: !service.is_active
            };
            
            // Remove variants from the service data if present
            const { variants, ...serviceDataWithoutVariants } = updatedService;
            
            const response = await axios.put(`${API_BASE_URL}/services/${service.id}`, serviceDataWithoutVariants);
            
            if (response.data.success || response.status === 200) {
                await fetchServices();
                setSuccessMessage(`Layanan berhasil ${updatedService.is_active ? 'diaktifkan' : 'dinonaktifkan'}`);
            }
        } catch (err) {
            console.error('Error updating service status:', err);
            setError('Gagal mengubah status layanan: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Handle add variant
    const handleAddVariant = async () => {
        try {
            if (!currentVariant.durasi_menit || !currentVariant.harga_dasar || !currentVariant.deskripsi_variant) {
                setError('Semua field varian harus diisi');
                return;
            }
            
            const newVariant = {
                ...currentVariant,
                service_id: currentService.id,
                durasi_menit: parseInt(currentVariant.durasi_menit),
                harga_dasar: parseInt(currentVariant.harga_dasar)
            };
            
            const response = await axios.post(`${API_BASE_URL}/service-variants`, newVariant);
            
            if (response.data.success || response.status === 201) {
                // Refresh the service with variants
                const updatedService = await fetchServiceWithVariants(currentService.id);
                setCurrentService(updatedService);
                resetVariantForm();
                setSuccessMessage('Varian layanan berhasil ditambahkan');
            }
        } catch (err) {
            console.error('Error adding variant:', err);
            setError('Gagal menambahkan varian: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Handle edit variant
    const handleEditVariant = async (variantId) => {
        try {
            if (!currentVariant.durasi_menit || !currentVariant.harga_dasar || !currentVariant.deskripsi_variant) {
                setError('Semua field varian harus diisi');
                return;
            }
            
            const updatedVariant = {
                ...currentVariant,
                service_id: currentService.id,
                durasi_menit: parseInt(currentVariant.durasi_menit),
                harga_dasar: parseInt(currentVariant.harga_dasar)
            };
            
            const response = await axios.put(`${API_BASE_URL}/service-variants/${variantId}`, updatedVariant);
            
            if (response.data.success || response.status === 200) {
                // Refresh the service with variants
                const updatedService = await fetchServiceWithVariants(currentService.id);
                setCurrentService(updatedService);
                resetVariantForm();
                setSuccessMessage('Varian layanan berhasil diperbarui');
            }
        } catch (err) {
            console.error('Error updating variant:', err);
            setError('Gagal memperbarui varian: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // Handle delete variant
    const handleDeleteVariant = async (variantId) => {
        try {
            const response = await axios.delete(`${API_BASE_URL}/service-variants/${variantId}`);
            
            if (response.data.success || response.status === 200) {
                // Refresh the service with variants
                const updatedService = await fetchServiceWithVariants(currentService.id);
                setCurrentService(updatedService);
                setSuccessMessage('Varian layanan berhasil dihapus');
            }
        } catch (err) {
            console.error('Error deleting variant:', err);
            setError('Gagal menghapus varian: ' + (err.response?.data?.message || err.message));
        }
    };
    
    // View service details with variants
    const viewServiceDetails = async (service) => {
        try {
            setLoading(true);
            const serviceWithVariants = await fetchServiceWithVariants(service.id);
            setCurrentService(serviceWithVariants);
            setImagePreview(serviceWithVariants.icon_url);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching service details:', err);
            setError(err.message);
            setLoading(false);
        }
    };
    
    // Get unique categories
    const categories = ['all', ...new Set(services.map(service => service.kategori).filter(Boolean))];
    
    // Filter services
    const filteredServices = services.filter(service => {
        const matchesStatus = filter === 'all' || 
                             (filter === 'active' && service.is_active) || 
                             (filter === 'inactive' && !service.is_active);
                             
        const matchesKategori = kategoriFilter === 'all' || service.kategori === kategoriFilter;
                             
        const matchesSearch = service.nama_layanan.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             service.deskripsi.toLowerCase().includes(searchTerm.toLowerCase());
                             
        return matchesStatus && matchesKategori && matchesSearch;
    });
    
    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredServices.slice(indexOfFirstItem, indexOfLastItem);
    
    const totalPages = Math.ceil(filteredServices.length / itemsPerPage);
    
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    
    // Render loading state
    if (loading && services.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }
    
    return (
        <>
            <div className="row gy-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-3">
                            <h4 className="mb-0 d-flex align-items-center">
                                <Icon icon="mdi:spa" className="me-2" />
                                Kelola Produk & Layanan
                            </h4>
                            <button
                                type="button"
                                className="btn btn-light d-flex align-items-center gap-2"
                                data-bs-toggle="modal"
                                data-bs-target="#addServiceModal"
                                onClick={resetForm}
                            >
                                <Icon icon="mdi:plus" />
                                Tambah Layanan
                            </button>
                        </div>
                        
                        <div className="card-body p-4">
                            {/* Success Message */}
                            {successMessage && (
                                <div className="alert alert-success alert-dismissible fade show" role="alert">
                                    {successMessage}
                                    <button 
                                        type="button" 
                                        className="btn-close" 
                                        onClick={() => setSuccessMessage(null)}
                                    ></button>
                                </div>
                            )}
                            
                            {/* Error Message */}
                            {error && (
                                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                    {error}
                                    <button 
                                        type="button" 
                                        className="btn-close" 
                                        onClick={() => setError(null)}
                                    ></button>
                                </div>
                            )}
                            
                            {/* Search and Filter */}
                            <div className="row mb-4">
                                <div className="col-md-4 mb-3 mb-md-0">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light">
                                            <Icon icon="mdi:magnify" />
                                        </span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Cari layanan..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setCurrentPage(1); // Reset to first page on new search
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-8 d-flex gap-2 flex-wrap">
                                    <select 
                                        className="form-select"
                                        value={filter}
                                        onChange={(e) => {
                                            setFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="all">Semua Status</option>
                                        <option value="active">Aktif</option>
                                        <option value="inactive">Tidak Aktif</option>
                                    </select>
                                    
                                    <select 
                                        className="form-select"
                                        value={kategoriFilter}
                                        onChange={(e) => {
                                            setKategoriFilter(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="all">Semua Kategori</option>
                                        {categories.filter(cat => cat !== 'all').map((category, index) => (
                                            <option key={index} value={category}>
                                                {category}
                                            </option>
                                        ))}
                                    </select>
                                    
                                    <button 
                                        className="btn btn-outline-secondary" 
                                        onClick={() => fetchServices()}
                                        title="Refresh Data"
                                    >
                                        <Icon icon="mdi:refresh" />
                                    </button>
                                </div>
                            </div>
                            
                            {/* Service Cards */}
                            <div className="row g-4">
                                {currentItems.length > 0 ? (
                                    currentItems.map(service => (
                                        <div key={service.id} className="col-xl-3 col-lg-4 col-md-6">
                                            <div className="card h-100 service-card border shadow-sm position-relative">
                                                {!service.is_active && (
                                                    <div className="position-absolute top-0 end-0 p-2">
                                                        <span className="badge bg-danger">Nonaktif</span>
                                                    </div>
                                                )}
                                                <div 
                                                    className="card-img-top position-relative" 
                                                    style={{ 
                                                        height: '180px', 
                                                        background: `url(${service.icon_url || 'https://via.placeholder.com/150?text=No+Image'}) center/cover no-repeat`,
                                                        borderTopLeftRadius: 'calc(0.375rem - 1px)',
                                                        borderTopRightRadius: 'calc(0.375rem - 1px)'
                                                    }}
                                                >
                                                    <div className="position-absolute bottom-0 start-0 w-100 p-2 text-white" style={{ background: 'rgba(0,0,0,0.7)' }}>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span className="badge bg-primary">
                                                                {service.kategori || 'Uncategorized'}
                                                            </span>
                                                            <span className="badge bg-info">
                                                                {service.durasi_menit} menit
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-body">
                                                    <h5 className="card-title text-primary fw-bold">{service.nama_layanan}</h5>
                                                    <h6 className="text-success fw-bold mb-2">
                                                        {formatPrice(service.harga_dasar)}
                                                    </h6>
                                                    <p className="card-text text-secondary small">
                                                        {service.deskripsi.length > 100 
                                                            ? `${service.deskripsi.substring(0, 100)}...` 
                                                            : service.deskripsi}
                                                    </p>
                                                </div>
                                                <div className="card-footer bg-transparent border-top-0">
                                                    <div className="d-flex justify-content-between">
                                                        <button 
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => {
                                                                viewServiceDetails(service);
                                                            }}
                                                            data-bs-toggle="modal"
                                                            data-bs-target="#viewServiceModal"
                                                        >
                                                            <Icon icon="mdi:eye" className="me-1" />
                                                            Detail
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-warning"
                                                            onClick={() => {
                                                                viewServiceDetails(service);
                                                            }}
                                                            data-bs-toggle="modal"
                                                            data-bs-target="#editServiceModal"
                                                        >
                                                            <Icon icon="mdi:pencil" className="me-1" />
                                                            Edit
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => setCurrentService(service)}
                                                            data-bs-toggle="modal"
                                                            data-bs-target="#deleteServiceModal"
                                                        >
                                                            <Icon icon="mdi:delete" className="me-1" />
                                                            Hapus
                                                        </button>
                                                    </div>
                                                    <div className="mt-2">
                                                        <button 
                                                            className={`btn btn-sm btn-outline-${service.is_active ? 'danger' : 'success'} w-100`}
                                                            onClick={() => toggleServiceStatus(service)}
                                                        >
                                                            <Icon 
                                                                icon={service.is_active ? "mdi:close-circle" : "mdi:check-circle"} 
                                                                className="me-1" 
                                                            />
                                                            {service.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-12 text-center py-5">
                                        <Icon icon="mdi:package-variant-remove" style={{ fontSize: '3rem' }} className="text-secondary" />
                                        <h5 className="mt-3">Tidak ada layanan yang ditemukan</h5>
                                        <p className="text-muted">
                                            {searchTerm || filter !== 'all' || kategoriFilter !== 'all'
                                                ? 'Coba ubah filter pencarian Anda' 
                                                : 'Silahkan tambahkan layanan baru'}
                                        </p>
                                    </div>
                                )}
                            </div>
                            
                            {/* Pagination */}
                            {filteredServices.length > itemsPerPage && (
                                <div className="d-flex justify-content-center mt-4">
                                    <nav aria-label="Page navigation">
                                        <ul className="pagination">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => paginate(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    &laquo;
                                                </button>
                                            </li>
                                            {[...Array(totalPages).keys()].map(number => (
                                                <li 
                                                    key={number + 1}
                                                    className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => paginate(number + 1)}
                                                    >
                                                        {number + 1}
                                                    </button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button 
                                                    className="page-link" 
                                                    onClick={() => paginate(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    &raquo;
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}
                            
                            {/* Counter */}
                            <div className="text-muted text-center mt-3">
                                Menampilkan {filteredServices.length > 0 ? indexOfFirstItem + 1 : 0} - {Math.min(indexOfLastItem, filteredServices.length)} dari {filteredServices.length} layanan
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Add Service Modal */}
            <div className="modal fade" id="addServiceModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Tambah Layanan Baru</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="addServiceModalClose"
                            ></button>
                        </div>
                        <form onSubmit={handleAddService}>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-8">
                                        <div className="mb-3">
                                            <label className="form-label">Nama Layanan</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Masukkan nama layanan"
                                                value={currentService.nama_layanan}
                                                onChange={(e) => setCurrentService({...currentService, nama_layanan: e.target.value})}
                                                required
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Deskripsi</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                placeholder="Deskripsikan layanan ini"
                                                value={currentService.deskripsi}
                                                onChange={(e) => setCurrentService({...currentService, deskripsi: e.target.value})}
                                                required
                                            ></textarea>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Kategori</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Contoh: Massage, Spa, dll"
                                                    value={currentService.kategori}
                                                    onChange={(e) => setCurrentService({...currentService, kategori: e.target.value})}
                                                    required
                                                    list="existing-categories"
                                                />
                                                <datalist id="existing-categories">
                                                    {categories.filter(cat => cat !== 'all').map((category, index) => (
                                                        <option key={index} value={category} />
                                                    ))}
                                                </datalist>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Status</label>
                                                <select 
                                                    className="form-select"
                                                    value={currentService.is_active}
                                                    onChange={(e) => setCurrentService({...currentService, is_active: e.target.value === 'true'})}
                                                >
                                                    <option value="true">Aktif</option>
                                                    <option value="false">Tidak Aktif</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Harga Dasar (Rp)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Contoh: 150000"
                                                    value={currentService.harga_dasar}
                                                    onChange={(e) => setCurrentService({...currentService, harga_dasar: e.target.value})}
                                                    required
                                                    min="0"
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Durasi (menit)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Contoh: 60"
                                                    value={currentService.durasi_menit}
                                                    onChange={(e) => setCurrentService({...currentService, durasi_menit: e.target.value})}
                                                    required
                                                    min="1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Icon Layanan</label>
                                            <input
                                                type="file"
                                                className="form-control mb-2"
                                                accept="image/*"
                                                onChange={handleImageSelect}
                                                required
                                            />
                                            <div className="border rounded p-2 text-center">
                                                {imagePreview ? (
                                                    <img
                                                        src={imagePreview}
                                                        alt="Preview"
                                                        className="img-fluid"
                                                        style={{ maxHeight: '200px' }}
                                                    />
                                                ) : (
                                                    <div className="py-5 text-muted">
                                                        <Icon icon="mdi:image" style={{ fontSize: '3rem' }} />
                                                        <p>Preview gambar</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="alert alert-info">
                                    <Icon icon="mdi:information" className="me-2" />
                                    Setelah layanan dibuat, Anda dapat menambahkan varian durasi dan harga.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    Simpan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            
            {/* View Service Modal with Variants */}
            <div className="modal fade" id="viewServiceModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Detail Layanan</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            {loading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="row mb-4">
                                        <div className="col-md-5 mb-3 mb-md-0">
                                            <img
                                                src={currentService.icon_url || 'https://via.placeholder.com/150?text=No+Image'}
                                                alt={currentService.nama_layanan}
                                                className="img-fluid rounded"
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://via.placeholder.com/150?text=Error+Loading+Image';
                                                }}
                                            />
                                        </div>
                                        <div className="col-md-7">
                                            <h4 className="text-primary">{currentService.nama_layanan}</h4>
                                            <div className="d-flex gap-2 mb-3">
                                                <span className="badge bg-primary">{currentService.kategori || 'Uncategorized'}</span>
                                                <span className="badge bg-info">{currentService.durasi_menit} menit</span>
                                                <span className={`badge ${currentService.is_active ? 'bg-success' : 'bg-danger'}`}>
                                                    {currentService.is_active ? 'Aktif' : 'Nonaktif'}
                                                </span>
                                            </div>
                                            <h5 className="text-success mb-3">{formatPrice(currentService.harga_dasar)}</h5>
                                            <h6 className="fw-bold">Deskripsi:</h6>
                                            <p className="text-secondary">{currentService.deskripsi}</p>
                                            <div className="mt-3 pt-3 border-top">
                                                <small className="text-muted">
                                                    Dibuat pada: {new Date(currentService.created_at).toLocaleString('id-ID', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Variants Section */}
                                    <div className="mt-4 pt-3 border-top">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="mb-0">Varian Layanan</h5>
                                            <button
                                                className="btn btn-sm btn-primary"
                                                data-bs-toggle="modal" 
                                                data-bs-target="#addVariantModal"
                                                onClick={() => {
                                                    resetVariantForm();
                                                }}
                                            >
                                                <Icon icon="mdi:plus" className="me-1" />
                                                Tambah Varian
                                            </button>
                                        </div>
                                        
                                        {currentService.variants && currentService.variants.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>Durasi</th>
                                                            <th>Harga</th>
                                                            <th>Deskripsi</th>
                                                            <th>Status</th>
                                                            <th className="text-end">Aksi</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentService.variants.map(variant => (
                                                            <tr key={variant.id}>
                                                                <td>{variant.durasi_menit} menit</td>
                                                                <td>{formatPrice(variant.harga_dasar)}</td>
                                                                <td>{variant.deskripsi_variant}</td>
                                                                <td>
                                                                    <span className={`badge ${variant.is_active ? 'bg-success' : 'bg-danger'}`}>
                                                                        {variant.is_active ? 'Aktif' : 'Nonaktif'}
                                                                    </span>
                                                                </td>
                                                                <td className="text-end">
                                                                    <button
                                                                        className="btn btn-sm btn-outline-warning me-1"
                                                                        onClick={() => {
                                                                            setCurrentVariant(variant);
                                                                        }}
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#editVariantModal"
                                                                    >
                                                                        <Icon icon="mdi:pencil" />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => {
                                                                            setCurrentVariant(variant);
                                                                        }}
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#deleteVariantModal"
                                                                    >
                                                                        <Icon icon="mdi:delete" />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="alert alert-warning">
                                                <Icon icon="mdi:alert" className="me-2" />
                                                Belum ada varian untuk layanan ini. Tambahkan varian untuk menawarkan durasi dan harga yang berbeda.
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Tutup
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                data-bs-toggle="modal"
                                data-bs-target="#editServiceModal"
                            >
                                Edit Layanan
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Edit Service Modal */}
            <div className="modal fade" id="editServiceModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Layanan</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="editServiceModalClose"
                            ></button>
                        </div>
                        <form onSubmit={handleEditService}>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-8">
                                        <div className="mb-3">
                                            <label className="form-label">Nama Layanan</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Masukkan nama layanan"
                                                value={currentService.nama_layanan}
                                                onChange={(e) => setCurrentService({...currentService, nama_layanan: e.target.value})}
                                                required
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Deskripsi</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                placeholder="Deskripsikan layanan ini"
                                                value={currentService.deskripsi}
                                                onChange={(e) => setCurrentService({...currentService, deskripsi: e.target.value})}
                                                required
                                            ></textarea>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Kategori</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Contoh: Massage, Spa, dll"
                                                    value={currentService.kategori}
                                                    onChange={(e) => setCurrentService({...currentService, kategori: e.target.value})}
                                                    required
                                                    list="existing-categories-edit"
                                                />
                                                <datalist id="existing-categories-edit">
                                                    {categories.filter(cat => cat !== 'all').map((category, index) => (
                                                        <option key={index} value={category} />
                                                    ))}
                                                </datalist>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Status</label>
                                                <select 
                                                    className="form-select"
                                                    value={currentService.is_active.toString()}
                                                    onChange={(e) => setCurrentService({...currentService, is_active: e.target.value === 'true'})}
                                                >
                                                    <option value="true">Aktif</option>
                                                    <option value="false">Tidak Aktif</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Harga Dasar (Rp)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Contoh: 150000"
                                                    value={currentService.harga_dasar}
                                                    onChange={(e) => setCurrentService({...currentService, harga_dasar: e.target.value})}
                                                    required
                                                    min="0"
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Durasi (menit)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Contoh: 60"
                                                    value={currentService.durasi_menit}
                                                    onChange={(e) => setCurrentService({...currentService, durasi_menit: e.target.value})}
                                                    required
                                                    min="1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <label className="form-label">Icon Layanan</label>
                                            <input
                                                type="file"
                                                className="form-control mb-2"
                                                accept="image/*"
                                                onChange={handleImageSelect}
                                            />
                                            <div className="border rounded p-2 text-center">
                                                {imagePreview ? (
                                                    <img
                                                        src={imagePreview}
                                                        alt="Preview"
                                                        className="img-fluid"
                                                        style={{ maxHeight: '200px' }}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://via.placeholder.com/150?text=Error+Loading+Image';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="py-5 text-muted">
                                                        <Icon icon="mdi:image" style={{ fontSize: '3rem' }} />
                                                        <p>Preview gambar</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="alert alert-info">
                                    <Icon icon="mdi:information" className="me-2" />
                                    Untuk mengelola varian layanan, silahkan gunakan menu detail layanan.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    Simpan Perubahan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            
            {/* Delete Service Modal */}
            <div className="modal fade" id="deleteServiceModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Konfirmasi Hapus</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="deleteServiceModalClose"
                            ></button>
                        </div>
                        <div className="modal-body text-center p-4">
                            <Icon 
                                icon="mdi:alert-circle" 
                                style={{ fontSize: '4rem' }} 
                                className="text-danger mb-3" 
                            />
                            <h5 className="mb-3">Apakah Anda yakin ingin menghapus layanan ini?</h5>
                            <p className="mb-1"><strong>{currentService.nama_layanan}</strong></p>
                            <p className="text-muted small mb-0">Tindakan ini tidak dapat dibatalkan</p>
                        </div>
                        <div className="modal-footer justify-content-center">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Batal
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDeleteService}
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Add Variant Modal */}
            <div className="modal fade" id="addVariantModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Tambah Varian Layanan</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="addVariantModalClose"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Durasi (menit)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Contoh: 90"
                                    value={currentVariant.durasi_menit}
                                    onChange={(e) => setCurrentVariant({...currentVariant, durasi_menit: e.target.value})}
                                    required
                                    min="1"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Harga (Rp)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Contoh: 200000"
                                    value={currentVariant.harga_dasar}
                                    onChange={(e) => setCurrentVariant({...currentVariant, harga_dasar: e.target.value})}
                                    required
                                    min="0"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Deskripsi Varian</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    placeholder="Contoh: Sesi extended 90 menit dengan perawatan lebih mendalam"
                                    value={currentVariant.deskripsi_variant}
                                    onChange={(e) => setCurrentVariant({...currentVariant, deskripsi_variant: e.target.value})}
                                    required
                                ></textarea>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Status</label>
                                <select 
                                    className="form-select"
                                    value={currentVariant.is_active.toString()}
                                    onChange={(e) => setCurrentVariant({...currentVariant, is_active: e.target.value === 'true'})}
                                >
                                    <option value="true">Aktif</option>
                                    <option value="false">Tidak Aktif</option>
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Batal
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    handleAddVariant();
                                    document.getElementById('addVariantModalClose').click();
                                }}
                            >
                                Simpan
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Edit Variant Modal */}
            <div className="modal fade" id="editVariantModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Varian Layanan</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="editVariantModalClose"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Durasi (menit)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Contoh: 90"
                                    value={currentVariant.durasi_menit}
                                    onChange={(e) => setCurrentVariant({...currentVariant, durasi_menit: e.target.value})}
                                    required
                                    min="1"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Harga (Rp)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Contoh: 200000"
                                    value={currentVariant.harga_dasar}
                                    onChange={(e) => setCurrentVariant({...currentVariant, harga_dasar: e.target.value})}
                                    required
                                    min="0"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Deskripsi Varian</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    placeholder="Contoh: Sesi extended 90 menit dengan perawatan lebih mendalam"
                                    value={currentVariant.deskripsi_variant}
                                    onChange={(e) => setCurrentVariant({...currentVariant, deskripsi_variant: e.target.value})}
                                    required
                                ></textarea>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Status</label>
                                <select 
                                    className="form-select"
                                    value={currentVariant.is_active.toString()}
                                    onChange={(e) => setCurrentVariant({...currentVariant, is_active: e.target.value === 'true'})}
                                >
                                    <option value="true">Aktif</option>
                                    <option value="false">Tidak Aktif</option>
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Batal
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    handleEditVariant(currentVariant.id);
                                    document.getElementById('editVariantModalClose').click();
                                }}
                            >
                                Simpan Perubahan
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Delete Variant Modal */}
            <div className="modal fade" id="deleteVariantModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Konfirmasi Hapus Varian</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                id="deleteVariantModalClose"
                            ></button>
                        </div>
                        <div className="modal-body text-center p-4">
                            <Icon 
                                icon="mdi:alert-circle" 
                                style={{ fontSize: '4rem' }} 
                                className="text-danger mb-3" 
                            />
                            <h5 className="mb-3">Apakah Anda yakin ingin menghapus varian ini?</h5>
                            <p className="mb-1"><strong>{currentVariant.durasi_menit} menit - {formatPrice(currentVariant.harga_dasar)}</strong></p>
                            <p className="text-muted small mb-0">Tindakan ini tidak dapat dibatalkan</p>
                        </div>
                        <div className="modal-footer justify-content-center">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                            >
                                Batal
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => {
                                    handleDeleteVariant(currentVariant.id);
                                    document.getElementById('deleteVariantModalClose').click();
                                }}
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProductServicesPage;